import Sb from "../abstract/StatefulBehavior";
import classNames from "classnames";

/*
RevealHeaderonScroll

This class handles the hiding and revealing of the site header bar. Currently
this is used only on the homepage. The gist of how it works is as follows:
the homepage has a second, larger header that appears below the main header bar.
Typically the header bar is sticky, but in this case it needs to be static
until the user has scrolled past the larger page header, at which point it
should slide in and become sticky. Rather than render multiple site header
components and toggle back and forth between them based on scroll position,
this class manipulates the visibility and position of the lone site header bar
based on the following state properties: 1) scroll direction; 2) whether the
user has scrolled past the site header bar by a certain threshold; and,
3) whether the user has scrolled far enough down the page that the sticky header
bar should slide into view. This class uses an intersection observer on the page
header (refs.scrollTarget) as a measure of if and when state should be updated.
*/

export default class RevealHeaderOnScroll extends Sb {
  constructor(el, props, refs) {
    super();

    this.state = {
      header: {
        scrolledPast: false,
        shouldEmerge: false,
      },
      scrollingDown: true,
    };

    this.el = el;
    this.props = props;
    this.refs = refs;

    if (!refs.scrollTarget || !refs.siteHeader) return;

    this.initSiteHeaderClass = refs.siteHeader.className;
    this.prevBoxY = 0;

    // This class depends on support of `position: sticky` in CSS.
    // If it's not supported, just use a static header instead.
    if (!this.supportsSticky) return this.useStaticFallback();

    this.runObserver();
    this.update();
  }

  get supportsSticky() {
    try {
      const supports = "CSS" in window && CSS.supports("position", "sticky");
      if (supports) return true;
    } catch (error) {
      return false;
    }

    return false;
  }

  /**
   * The scroll past threshhold should be slightly smaller than the height
   * of the scroll target element.
   * If it's the exact height or larger, the intersection observer might not
   * catch when we've scrolled past the element.
   */
  get scrollPastThreshold() {
    return (this.refs.scrollTarget.offsetHeight || 200) - 10;
  }

  get intersectionThreshold() {
    return 0.3;
  }

  get observerOptions() {
    return {
      rootMargin: `0px 0px 0px 0px`,
      // [0, 0.1, 0.2, 0.3, ...]
      threshold: [...Array(10 + 1).keys()].map((x) => x / 10),
    };
  }

  update = () => {
    const { siteHeader, siteLogo } = this.refs;
    const {
      header: { scrolledPast, shouldEmerge },
      scrollingDown,
    } = this.state;

    siteHeader.className = classNames(this.initSiteHeaderClass, {
      "is-static": !scrolledPast,
      "is-sticky": scrolledPast,
      "is-transformed": shouldEmerge,
      "is-untransformed": !shouldEmerge,
      "is-scrolling-down": scrollingDown,
      "is-scrolling-up": !scrollingDown,
    });

    if (!siteLogo) return;

    siteLogo.className = classNames({
      "is-invisible": !scrolledPast,
      "is-visible": scrolledPast,
    });
    siteLogo.setAttribute("tabindex", !scrolledPast ? -1 : 0);
    siteLogo.setAttribute("aria-hidden", !scrolledPast);
  };

  runObserver() {
    this.observer = new IntersectionObserver(
      this.onIntersection,
      this.observerOptions
    );
    this.observer.observe(this.refs.scrollTarget);
  }

  onIntersection = (entries) => {
    const entry = entries[0];
    this.setState({
      header: {
        scrolledPast: this.isScrolledPast(entry),
        shouldEmerge: this.shouldEmerge(entry),
      },
      scrollingDown: this.isScrollingDown(entry),
    });
    this.prevBoxY = entry.boundingClientRect.y;
  };

  isScrolledPast(entry) {
    return entry.boundingClientRect.y < -1 * this.scrollPastThreshold;
  }

  shouldEmerge(entry) {
    return (
      this.isScrolledPast(entry) &&
      entry.intersectionRatio > this.intersectionThreshold
    );
  }

  isScrollingDown({ boundingClientRect }) {
    return boundingClientRect.y < this.prevBoxY;
  }

  useStaticFallback() {
    const { siteHeader, siteLogo } = this.refs;

    siteHeader.className = classNames(this.initSiteHeaderClass, {
      "is-static": true,
      "is-untransformed": true,
    });

    if (!siteLogo) return;

    siteLogo.setAttribute("tabindex", -1);
    siteLogo.setAttribute("aria-hidden", true);
  }
}
